package fr.frux.core.designsystem.components

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.selection.selectable
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Check
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.minimumInteractiveComponentSize
import androidx.compose.material3.ripple
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.theme.FruxSpacing

@Composable
fun FruxFilterChip(
    label: String,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    leadingEmoji: String? = null,
) {
    val shape = RoundedCornerShape(50)
    val containerColor =
        if (selected) {
            MaterialTheme.colorScheme.primary
        } else {
            MaterialTheme.colorScheme.surface
        }
    val contentColor =
        if (selected) {
            MaterialTheme.colorScheme.onPrimary
        } else {
            MaterialTheme.colorScheme.primary
        }
    val interactionSource = remember { MutableInteractionSource() }

    Row(
        modifier =
            modifier
                .minimumInteractiveComponentSize()
                .heightIn(min = 36.dp)
                .clip(shape)
                .background(containerColor)
                .then(
                    if (selected) {
                        Modifier
                    } else {
                        Modifier.border(1.dp, MaterialTheme.colorScheme.outlineVariant, shape)
                    },
                ).selectable(
                    selected = selected,
                    onClick = onClick,
                    role = Role.Checkbox,
                    interactionSource = interactionSource,
                    indication = ripple(),
                ).padding(PaddingValues(horizontal = 12.dp)),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        // L'icône Check vectorielle remplace l'emoji fruit quand sélectionné (convention M3 FilterChip :
        // un seul indicateur leading à la fois). Icône plutôt que glyphe texte "✓" : absent des fontes
        // embarquées Fraunces/Inter, rendu en tofu sur wasm (pas de fallback système sur ce target).
        if (selected) {
            Icon(
                imageVector = Icons.Outlined.Check,
                contentDescription = null,
                modifier = Modifier.size(16.dp),
                tint = contentColor,
            )
            Spacer(Modifier.width(FruxSpacing.xs))
        } else if (leadingEmoji != null) {
            Text(
                text = "$leadingEmoji ",
                style = MaterialTheme.typography.labelLarge,
                color = contentColor,
            )
        }
        Text(
            text = label,
            style = MaterialTheme.typography.labelLarge,
            color = contentColor,
        )
    }
}

/**
 * Action posée dans une rangée de [FruxFilterChip] — elle navigue au lieu de filtrer.
 *
 * Vit ici, et non chez l'appelant, pour que sa gabarit ne puisse pas diverger de celui des chips
 * de filtre qui l'entourent : mêmes hauteur, rayon, padding et typographie. Comme eux, 36 dp
 * visuels avec une cible tactile portée à 48 dp par `minimumInteractiveComponentSize()` — la
 * hauteur perçue reste celle de la rangée sans sacrifier l'accessibilité.
 *
 * Toujours une surface opaque : ces rangées flottent au-dessus de la carte, où un libellé nu
 * verrait son contraste dépendre des tuiles sous-jacentes.
 */
@Composable
fun FruxActionChip(
    label: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    // Icône de tête plutôt qu'un emoji dans le label : Compose js ne rend pas les emojis (#169).
    leadingIcon: ImageVector? = null,
    trailingIcon: ImageVector? = null,
) {
    val shape = RoundedCornerShape(50)
    val contentColor = MaterialTheme.colorScheme.primary
    val interactionSource = remember { MutableInteractionSource() }

    Row(
        modifier =
            modifier
                .minimumInteractiveComponentSize()
                .heightIn(min = 36.dp)
                .clip(shape)
                .background(MaterialTheme.colorScheme.surface)
                .border(1.dp, MaterialTheme.colorScheme.outlineVariant, shape)
                .clickable(
                    onClick = onClick,
                    role = Role.Button,
                    interactionSource = interactionSource,
                    indication = ripple(),
                ).padding(PaddingValues(horizontal = 12.dp)),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        if (leadingIcon != null) {
            Icon(
                imageVector = leadingIcon,
                contentDescription = null,
                modifier = Modifier.size(16.dp),
                tint = contentColor,
            )
            Spacer(Modifier.width(FruxSpacing.xs))
        }
        Text(
            text = label,
            style = MaterialTheme.typography.labelLarge,
            color = contentColor,
        )
        if (trailingIcon != null) {
            Spacer(Modifier.width(FruxSpacing.xs))
            Icon(
                imageVector = trailingIcon,
                contentDescription = null,
                modifier = Modifier.size(16.dp),
                tint = contentColor,
            )
        }
    }
}
